import React, { useEffect, useState } from 'react';
import { ArrowUpRight, Newspaper } from 'lucide-react';
import API from '../lib/api';
import NewsDetailModal from './NewsDetailModal';

const categoriesDefault = ['All', 'Announcement', 'Partnership', 'Technology', 'Community', 'Engineering'];

const NewsPage = () => {
  const [cat, setCat] = useState('All');
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get('/news');
        setItems(data.items || []);
      } catch {}
    })();
  }, []);

  const openArticle = async (item) => {
    try {
      const { data } = await API.get(`/news/${item.id}`);
      setActive(data.news || item);
    } catch {
      setActive(item);
    }
  };

  const filtered = cat === 'All' ? items : items.filter((n) => n.category === cat);
  const featured = items.find((n) => n.featured) || items[0];

  return (
    <section className="px-4 md:px-10 py-6 pb-16">
      <div className="mx-auto max-w-[1240px]">
        <div className="rounded-3xl bg-[#fbf4dc] border border-emerald-900/15 shadow-2xl overflow-hidden">
          <div className="p-6 md:p-8 lg:p-10">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-2xl bg-emerald-950 flex items-center justify-center text-emerald-300">
                  <Newspaper size={20} />
                </div>
                <div>
                  <h2 className="font-serif text-[28px] md:text-[32px] text-emerald-950 tracking-tight leading-none">Ecosystem News</h2>
                  <div className="mt-1 text-[12px] text-emerald-900/60">Curated updates from the SkyWay Green global ecosystem.</div>
                </div>
              </div>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-800/40 bg-emerald-200/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-900">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-600" /> Real Estate
              </span>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {categoriesDefault.map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition ${cat === c ? 'bg-emerald-950 text-emerald-50' : 'bg-emerald-100/60 text-emerald-900 hover:bg-emerald-100'}`}
                >
                  {c}
                </button>
              ))}
            </div>

            {cat === 'All' && featured && (
              <article
                onClick={() => openArticle(featured)}
                className="mt-8 grid md:grid-cols-2 gap-6 rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] overflow-hidden cursor-pointer hover:border-emerald-700/40 transition"
                data-testid={`news-featured-${featured.id}`}
              >
                <div className="aspect-[16/10] md:aspect-auto overflow-hidden">
                  <img src={featured.image} alt={featured.title} className="h-full w-full object-cover" loading="lazy" />
                </div>
                <div className="p-6 md:p-8 flex flex-col justify-center">
                  <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest">
                    <span className="rounded-full bg-emerald-950 text-emerald-100 px-2.5 py-0.5">Featured</span>
                    <span className="text-emerald-700">{featured.category}</span>
                    <span className="text-emerald-900/40">•</span>
                    <span className="text-emerald-900/60">{featured.date}</span>
                  </div>
                  <h3 className="mt-3 font-serif text-emerald-950 text-2xl md:text-3xl leading-tight">{featured.title}</h3>
                  <p className="mt-3 text-[14px] text-emerald-900/70 leading-relaxed">{featured.excerpt}</p>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); openArticle(featured); }}
                    className="mt-5 self-start inline-flex items-center gap-1.5 rounded-xl bg-emerald-950 hover:bg-emerald-900 text-emerald-50 text-sm font-medium px-4 py-2.5 transition"
                    data-testid={`news-featured-read-${featured.id}`}
                  >
                    Read full story <ArrowUpRight size={14} />
                  </button>
                </div>
              </article>
            )}

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.filter((n) => cat !== 'All' || !featured || n.id !== featured.id).map((n) => (
                <article
                  key={n.id}
                  onClick={() => openArticle(n)}
                  className="group rounded-2xl overflow-hidden border border-emerald-900/15 bg-[#fdf9e7] hover:border-emerald-700/40 transition cursor-pointer"
                  data-testid={`news-card-${n.id}`}
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={n.image} alt={n.title} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition duration-700" />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest">
                      <span className="text-emerald-700">{n.category}</span>
                      <span className="text-emerald-900/40">•</span>
                      <span className="text-emerald-900/60">{n.date}</span>
                    </div>
                    <h3 className="mt-2.5 font-serif text-emerald-950 text-lg leading-snug">{n.title}</h3>
                    <p className="mt-1.5 text-[13px] text-emerald-900/65 leading-relaxed line-clamp-2">{n.excerpt}</p>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); openArticle(n); }}
                      className="mt-3 inline-flex items-center gap-1 text-emerald-800 hover:text-emerald-700 text-sm font-medium"
                      data-testid={`news-read-${n.id}`}
                    >
                      Read article <ArrowUpRight size={13} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
                    </button>
                  </div>
                </article>
              ))}
              {items.length === 0 && (
                <div className="col-span-full text-center text-emerald-900/60 text-sm py-10">No news yet.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {active && <NewsDetailModal news={active} onClose={() => setActive(null)} />}
    </section>
  );
};

export default NewsPage;
